import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  type ComputedRef,
  type InjectionKey,
} from 'vue';
import type { NxToastPlacement } from './NxToastHost.vue';

export type NxToastKind = 'success' | 'error' | 'info';

/** Why a toast left the screen — handed to `NxToastOptions.onClose`. */
export type NxToastCloseReason = 'timeout' | 'action' | 'dismiss';

/** Optional call-to-action rendered as a button inside the toast ("Undo", "Open", …). */
export interface NxToastAction {
  label: string;
  /** Runs on click; the toast is dismissed right after (reason `'action'`) unless `keepOpen` is set. */
  onClick: () => void;
  /**
   * The action runs but the toast stays — for actions that do not answer the toast's cause, e.g.
   * copying an id. A persistent toast then still closes only via dismiss or an action without
   * `keepOpen`; the timeout of a timed toast is unaffected.
   */
  keepOpen?: boolean;
}

export interface NxToastOptions {
  message: string;
  /** Visual tone. Defaults to `'success'`. */
  kind?: NxToastKind;
  /** Auto-dismiss delay in ms. Defaults to 3000; ignored when `persistent`. */
  timeout?: number;
  /** Action button shown next to the message. */
  action?: NxToastAction;
  /** Never auto-dismiss — the toast stays until the user dismisses it or acts on it. */
  persistent?: boolean;
  /**
   * Show the remaining seconds and a shrinking progress bar driven by
   * `timeout` (typical for "will send in 5 s — Undo"). Requires a timeout.
   */
  countdown?: boolean;
  /** Whether the toast offers a dismiss control / click-to-dismiss. Defaults to `true`. */
  dismissible?: boolean;
  /** Called once when the toast leaves, with the reason. */
  onClose?: (reason: NxToastCloseReason) => void;
}

export interface NxToast {
  id: number;
  message: string;
  kind: NxToastKind;
  action?: NxToastAction;
  persistent: boolean;
  countdown: boolean;
  dismissible: boolean;
  /** Auto-dismiss delay in ms (`0` when persistent). */
  timeout: number;
  /** Epoch ms when the toast auto-dismisses; `null` when persistent. */
  expiresAt: number | null;
  onClose?: (reason: NxToastCloseReason) => void;
}

const toasts = ref<NxToast[]>([]);
// The handle type follows the scheduler in use: a number in the browser, a
// Timeout object under Node types, which is what the test runner compiles with.
const timers = new Map<number, ReturnType<typeof setTimeout>>();
let seq = 0;

/** What a host looks like: the placement and the dismiss label it renders with. */
export interface NxToastHostLook {
  placement: NxToastPlacement;
  dismissLabel: string;
}

interface NxToastHostEntry {
  token: symbol;
  /** An inheriting host (a modal's own) takes the look of the host it covers. */
  inherit: boolean;
  look: () => NxToastHostLook;
}

// Shallow: entries are compared by identity, a deep proxy would never equal the raw entry.
const hostStack = shallowRef<NxToastHostEntry[]>([]);

/**
 * Provided by a modal's own toast host: it renders where and how the page's host does, so a
 * toast does not jump when a modal opens. Only for the host directly below the provider.
 */
export const nxToastHostInheritKey: InjectionKey<boolean> = Symbol('nx-toast-host-inherit');

/** Clears the module-level host stack (tests / HMR teardown). */
export function resetNxToastHostStack(): void {
  hostStack.value = [];
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    resetNxToastHostStack();
  });
}

/**
 * Host coordination: with nested hosts (Connect shell + a modal's own), only one host renders the
 * shared toast list — otherwise every toast would appear once per host. While a modal's
 * (inheriting) host is registered, the newest of those is on top, so a page host that mounts
 * behind an open modal does not pull the toasts out of the dialog; otherwise the newest host is.
 */
function isTopHost(entry: NxToastHostEntry): boolean {
  const stack = hostStack.value;
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].inherit) return stack[i] === entry;
  }
  return stack.at(-1) === entry;
}

function registerHost(entry: NxToastHostEntry): ComputedRef<boolean> {
  onMounted(() => {
    hostStack.value = [...hostStack.value, entry];
  });
  onBeforeUnmount(() => {
    hostStack.value = hostStack.value.filter((e) => e !== entry);
  });
  return computed(() => isTopHost(entry));
}

/**
 * Presence plus the look to render with: the host's own, or — `inherit` — that of the nearest
 * host below it in the stack which is not inheriting itself (nested modals reach through to
 * the page's host). With none below, its own.
 */
export function useNxToastHost(own: () => NxToastHostLook, inherit: boolean) {
  const entry: NxToastHostEntry = { token: Symbol('nx-toast-host'), inherit, look: own };
  const isTop = registerHost(entry);
  const look = computed<NxToastHostLook>(() => {
    if (inherit) {
      const stack = hostStack.value;
      for (let i = stack.indexOf(entry) - 1; i >= 0; i--) {
        const below = stack[i];
        if (!below.inherit) return below.look();
      }
    }
    return own();
  });
  return { isTop, look };
}

// Timers go through globalThis, not window: a toast scheduled by a component
// test keeps ticking after vitest tore the jsdom window down, and a timer that
// then reached for `window` threw "window is not defined" as an unhandled
// error and failed a run in which every test had passed.
function removeToast(id: number, reason: NxToastCloseReason): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    globalThis.clearTimeout(timer);
    timers.delete(id);
  }
  const toast = toasts.value.find((t) => t.id === id);
  if (!toast) return;
  toasts.value = toasts.value.filter((t) => t.id !== id);
  toast.onClose?.(reason);
}

export function useNxToasts() {
  /** Removes a toast (reason `'dismiss'`); unknown ids are a no-op. */
  function dismiss(id: number) {
    removeToast(id, 'dismiss');
  }

  /** Runs the toast's action (if any) and removes it (reason `'action'`) unless it has `keepOpen`. */
  function runAction(id: number) {
    const toast = toasts.value.find((t) => t.id === id);
    if (!toast) return;
    toast.action?.onClick();
    if (toast.action?.keepOpen) return;
    removeToast(id, 'action');
  }

  /**
   * Shows a toast and returns its id (for `dismiss`).
   *
   * Positional form: `showToast('Saved', 'success', 3000)`.
   * Options form: `showToast({ message, kind, timeout, action, persistent, countdown, onClose })`.
   */
  function showToast(options: NxToastOptions): number;
  function showToast(message: string, kind?: NxToastKind, timeout?: number): number;
  function showToast(
    messageOrOptions: string | NxToastOptions,
    kind: NxToastKind = 'success',
    timeout = 3000
  ): number {
    const options: NxToastOptions =
      typeof messageOrOptions === 'string'
        ? { message: messageOrOptions, kind, timeout }
        : messageOrOptions;
    const id = ++seq;
    const persistent = options.persistent === true;
    const delay = persistent ? 0 : Math.max(0, options.timeout ?? 3000);
    toasts.value.push({
      id,
      message: options.message,
      kind: options.kind ?? 'success',
      action: options.action,
      persistent,
      countdown: options.countdown === true && !persistent,
      dismissible: options.dismissible !== false,
      timeout: delay,
      expiresAt: persistent ? null : Date.now() + delay,
      onClose: options.onClose,
    });
    if (!persistent) {
      timers.set(
        id,
        globalThis.setTimeout(() => removeToast(id, 'timeout'), delay)
      );
    }
    return id;
  }

  return { toasts, showToast, dismiss, runAction };
}
